import React, { useState } from 'react';
import { useBusinessContext } from '../contexts/BusinessContext';
import { businessApi } from '../services/businessApi';
import './BusinessSetupDialog.css';

export function BusinessSetupDialog() {
  const { 
    isBusinessSetupDialogOpen, 
    closeBusinessSetupDialog, 
    businessDetails,
    openPricingDialog,
    openWebsiteStudioDialog,
    setBusinessDetails
  } = useBusinessContext();

  const [siteName, setSiteName] = useState('');
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState<'idle' | 'available' | 'unavailable'>('idle');
  const [availabilityMessage, setAvailabilityMessage] = useState('');

  const handleBackToPricing = () => {
    closeBusinessSetupDialog();
    openPricingDialog();
  };

  const handleCheckAvailability = async () => {
    if (!siteName.trim()) {
      setAvailabilityMessage('Please enter a site name before checking availability.');
      setAvailabilityStatus('unavailable');
      return;
    }

    setIsCheckingAvailability(true);
    setAvailabilityStatus('idle');

    try {
      const response = await businessApi.checkSiteNameAvailability({
        site_name: siteName.trim()
      });

      if (response.success && response.data) {
        if (response.data.available) {
          setAvailabilityStatus('available');
          setAvailabilityMessage(response.data.message);
        } else {
          setAvailabilityStatus('unavailable');
          setAvailabilityMessage(response.data.message);
        }
      } else {
        throw new Error(response.message || 'Failed to check availability');
      }
    } catch (error: any) {
      console.error('Failed to check site name availability:', error);
      setAvailabilityStatus('unavailable');
      setAvailabilityMessage('Failed to check availability. Please try again.');
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const handleNext = () => {
    if (!siteName.trim()) {
      setAvailabilityMessage('Please enter a site name.');
      setAvailabilityStatus('unavailable');
      return;
    }

    if (availabilityStatus !== 'available') {
      setAvailabilityMessage('Please check site name availability before proceeding.');
      return;
    }

    setBusinessDetails({
      ...businessDetails!,
      siteName: siteName.trim()
    });

    closeBusinessSetupDialog();
    openWebsiteStudioDialog();
  };

  if (!isBusinessSetupDialogOpen || !businessDetails) return null;

  const getCurrencySymbol = () => {
    const currencyMap: Record<string, string> = {
      'PH': '₱',
      'ID': 'Rp',
      'VN': '₫',
      'IN': '₹',
      'BD': '৳',
      'MY': 'RM',
      'TH': '฿',
      'MX': '$',
      'GL': '$'
    };
    return currencyMap[businessDetails.countryCode] || '$';
  };

  const getPlanPrice = () => {
    if (businessDetails.selectedPricingPlan?.priceDisplay) {
      return businessDetails.selectedPricingPlan.priceDisplay;
    }
    if (businessDetails.selectedPricingPlan?.setupFee?.amount) {
      return businessDetails.selectedPricingPlan.setupFee.amount;
    }
    if (businessDetails.selectedPricingPlan?.subscription?.amount) {
      return businessDetails.selectedPricingPlan.subscription.amount;
    }
    return businessDetails.selectedPlan === 'Standard Plan' ? '999' : '1999';
  };

  const getSettlementMethod = () => {
    if (businessDetails.selectedPricingPlan?.features?.length) {
      const settlementFeature = businessDetails.selectedPricingPlan.features.find((text) =>
        text.toLowerCase().includes('settlement')
      );
      if (settlementFeature) {
        return settlementFeature;
      }
    }
    // Fallback
    return businessDetails.selectedPlan === 'Standard Plan' 
      ? 'Settlement Method: Daily Settlement'
      : 'Settlement Method: Monthly Settlement';
  };

  const getOperationDeposit = () => {
    if (businessDetails.selectedPricingPlan?.features?.length) {
      const depositFeature = businessDetails.selectedPricingPlan.features.find((text) =>
        text.toLowerCase().includes('deposit') || text.toLowerCase().includes('purchase')
      );
      if (depositFeature) {
        return depositFeature;
      }
    }
    // Fallback
    return 'Operation Deposit: 1000 USD / Minimum Purchase';
  };

  return (
    <div className="business-setup-overlay" onClick={closeBusinessSetupDialog}>
      <div className="business-setup-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="business-setup-header">
          <button className="business-setup-back" onClick={handleBackToPricing}>
            ←
          </button>
          <button className="business-setup-close" onClick={closeBusinessSetupDialog}>
            ×
          </button>
        </div>

        <div className="business-setup-content">
          <h2 className="business-setup-title">Business Setup</h2>

          <div className="business-setup-grid">
            <div className="business-setup-summary">
              <div className="business-setup-summary-card">
                <label className="business-setup-label">Country (Currency)</label>
                <div className="business-setup-country-info">
                  <span className="business-setup-flag">{businessDetails.flag}</span>
                  <span className="business-setup-country-name">
                    {businessDetails.country} ({getCurrencySymbol()})
                  </span>
                </div>
              </div>

              <div className="business-setup-summary-card">
                <div className="business-setup-plan-header">
                  <span className="business-setup-label">Selected Plan</span>
                  <span className="business-setup-plan-badge">
                    {businessDetails.selectedPricingPlan?.name || businessDetails.selectedPlan || 'N/A'}
                  </span>
                </div>
                <div className="business-setup-plan-details">
                  <div className="business-setup-plan-price">{getPlanPrice()}</div>
                  {businessDetails.selectedPricingPlan?.description && (
                    <div className="business-setup-plan-description">
                      {businessDetails.selectedPricingPlan.description}
                    </div>
                  )}
                  <div className="business-setup-plan-note">
                    {getSettlementMethod()}
                  </div>
                  <div className="business-setup-plan-note">
                    {getOperationDeposit()}
                  </div>
                </div>
              </div>
            </div>

            <div className="business-setup-form">
              <div className="business-setup-form-card">
                <label className="business-setup-label">* Site Name</label>
                <div className="business-setup-input-group">
                  <input
                    type="text"
                    placeholder="Please enter site name"
                    value={siteName}
                    onChange={(e) => {
                      setSiteName(e.target.value);
                      if (availabilityStatus !== 'idle') {
                        setAvailabilityStatus('idle');
                        setAvailabilityMessage('');
                      }
                    }}
                    className={`business-setup-input ${
                      availabilityStatus === 'available' 
                        ? 'business-setup-input-available' 
                        : availabilityStatus === 'unavailable'
                        ? 'business-setup-input-unavailable'
                        : ''
                    }`}
                  />
                  <button
                    type="button"
                    onClick={handleCheckAvailability}
                    disabled={isCheckingAvailability || !siteName.trim()}
                    className="business-setup-check-button"
                  >
                    {isCheckingAvailability ? '...' : 'Check'}
                  </button>
                </div>
                
                {availabilityStatus !== 'idle' && (
                  <div className={`business-setup-availability ${
                    availabilityStatus === 'available' 
                      ? 'business-setup-availability-available' 
                      : 'business-setup-availability-unavailable'
                  }`}>
                    {availabilityStatus === 'available' ? '✓' : '✗'} {availabilityMessage}
                  </div>
                )}
                
                <p className="business-setup-hint">
                  This will be your website's domain name
                </p>
              </div>

              <button
                onClick={handleNext}
                disabled={availabilityStatus !== 'available'}
                className={`business-setup-next ${
                  availabilityStatus === 'available'
                    ? 'business-setup-next-enabled'
                    : 'business-setup-next-disabled'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

